export type Locale = "ru" | "en";

const INTL: Record<Locale, string> = { ru: "ru-RU", en: "en-US" };

export function intlLocale(locale: Locale): string {
  return INTL[locale] || INTL.ru;
}

/** Целое/дробное число с ограничением знаков. Неизвестное значение → «—». */
export function formatNumber(
  value: number | null | undefined,
  locale: Locale = "ru",
  maxFraction = 2,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "—";
  return new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: maxFraction,
  }).format(value);
}

/** Процент. По умолчанию целый (кланы), `maxFraction` — для статистики. */
export function formatPercent(
  value: number | null | undefined,
  locale: Locale = "ru",
  maxFraction = 0,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "—";
  return `${formatNumber(value, locale, maxFraction)}%`;
}

/** Секунды → m:ss / h:mm:ss. Неизвестная длительность → «—», а не 0:00. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds))
    return "—";
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  locale: Locale = "ru",
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
): string {
  if (value === null || value === undefined || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(intlLocale(locale), options);
}

const MAP_NAMES: Record<string, string> = {
  dust2: "Dust II",
  mirage: "Mirage",
  inferno: "Inferno",
  nuke: "Nuke",
  overpass: "Overpass",
  vertigo: "Vertigo",
  ancient: "Ancient",
  anubis: "Anubis",
  train: "Train",
  cache: "Cache",
  cobblestone: "Cobblestone",
  office: "Office",
  italy: "Italy",
  agency: "Agency",
};

/** de_dust2 → Dust II; неизвестные карты — читаемое имя из id. */
export function displayMap(map: string | null | undefined): string {
  if (!map) return "—";
  const id = map.replace(/^(de|cs|ar)_/i, "").toLowerCase();
  if (MAP_NAMES[id]) return MAP_NAMES[id];
  return id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Русское склонение: plural(5, ["матч", "матча", "матчей"]). */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}
